import CrewSettings from "@/components/CrewSettings";
import { GENRES } from "@/lib/constants";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Upload, Instagram, Facebook } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Avatar } from "@/components/AttendeesModal";


export default function Profile() {
  const { user, t, lang, setLang, theme, setTheme, refreshMe, logout, checkingAuth } = useApp();
  const [p, setP] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { if (user) setP({ ...user }); }, [user]);
  if (checkingAuth) return null;
  if (!user) return <Navigate to="/auth" />;
  if (!p) return null;
  const pp = { show_location: true, show_bio: true, show_genres: true, ...(p.public_profile || {}) };
  const setPP = (k, v) => setP({ ...p, public_profile: { ...pp, [k]: v } });
  const toggleGenre = g => setP({ ...p, preferred_genres: (p.preferred_genres || []).includes(g)
    ? p.preferred_genres.filter(x => x !== g) : [...(p.preferred_genres || []), g] });

  const save = async () => {
    try {
    await api.put("/auth/profile", {
      name: p.name, language: p.language, theme: p.theme,
      location_city: p.location_city, location_country: p.location_country,
      preferred_genres: p.preferred_genres, notif_prefs: p.notif_prefs,
      favourite_tracks: (p.favourite_tracks_str ?? (p.favourite_tracks || []).join(", ")).split(",").map(x => x.trim()).filter(Boolean).slice(0, 3),
      favourite_events: (p.favourite_events_str ?? (p.favourite_events || []).join(", ")).split(",").map(x => x.trim()).filter(Boolean).slice(0, 3),
      bio: p.bio ?? "", is_dj: !!p.is_dj, dj_name: p.is_dj ? (p.dj_name || p.name) : "", attendance_public: p.attendance_public ?? true, public_profile: p.public_profile, accept_messages: p.accept_messages ?? true, instagram: p.instagram ?? "", facebook: p.facebook ?? "",
    });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); return; }
    setLang(p.language); setTheme(p.theme);
    await refreshMe();
    toast.success(t("savedProfile"));
  };
  const del = async () => {
    await api.delete("/auth/account");
    await logout();
    window.location.href = "/";
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter border-b border-foreground pb-3">{t("profile")}</h1>
      <div className="mt-6 flex items-center gap-4">
        <Avatar user={p} size="w-20 h-20 text-2xl" />
        <div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="avatar-file-input"
            onChange={async e => { const file = e.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append("file", file);
              try { await api.post("/uploads/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); await refreshMe(); toast.success(t("avatarUpdated")); }
              catch (err) { toast.error(err.response?.data?.detail || t("uploadFailed")); } e.target.value = ""; }} />
          <button type="button" onClick={() => fileRef.current?.click()} className="gg-btn !py-2 text-xs" data-testid="avatar-upload-btn"><Upload className="w-4 h-4" />{t("uploadAvatar")}</button>
          <div className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP · max 3 MB</div>
        </div>
      </div>
      <h2 className="eyebrow text-primary mt-8 border-t border-foreground pt-6" data-testid="public-section">{t("publicProfile")}</h2>
      <div className="mt-4 grid md:grid-cols-2 gap-6">
        <div>
          <label className="eyebrow">{t("yourName")}</label>
          <input className="gg-input mt-2" data-testid="p-name" value={p.name} onChange={e => setP({ ...p, name: e.target.value })} />
        </div>
        <div>
          <label className="eyebrow">{t("locationCity")}</label>
          <input className="gg-input mt-2" value={p.location_city || ""} onChange={e => setP({ ...p, location_city: e.target.value })} />
        </div>
        <div>
          <label className="eyebrow">{t("locationCountry")}</label>
          <input className="gg-input mt-2" value={p.location_country || ""} onChange={e => setP({ ...p, location_country: e.target.value })} />
        </div>
      </div>
      <h2 className="eyebrow text-primary mt-8 border-t border-foreground pt-6" data-testid="settings-section">{t("settings")}</h2>
      <div className="mt-4 grid md:grid-cols-2 gap-6">
        <div>
          <label className="eyebrow">{t("email")}</label>
          <input disabled className="gg-input mt-2" value={p.email} />
        </div>
        <div>
          <label className="eyebrow">{t("languageLabel")}</label>
          <select className="gg-input mt-2" value={p.language} onChange={e => setP({ ...p, language: e.target.value })}>
            <option value="en">English</option><option value="de">Deutsch</option>
          </select>
        </div>
        <div>
          <label className="eyebrow">{t("themeLabel")}</label>
          <select className="gg-input mt-2" value={p.theme} onChange={e => setP({ ...p, theme: e.target.value })}>
            <option value="light">{t("lightMode")}</option><option value="dark">{t("darkMode")}</option>
          </select>
        </div>
      </div>
      <div className="mt-6">
        <label className="eyebrow">{t("preferredGenres")}</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {GENRES.map(g => (
            <button key={g} onClick={() => toggleGenre(g)} className={`gg-chip ${(p.preferred_genres || []).includes(g) ? "gg-chip-active" : ""}`}>
              {t(`genres.${g}`)}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6 border border-foreground p-3" data-testid="dj-section">
        <label className="flex items-center gap-3 cursor-pointer min-h-[40px]">
          <input type="checkbox" className="w-4 h-4" checked={!!p.is_dj} onChange={e => setP({ ...p, is_dj: e.target.checked })} data-testid="p-is-dj" />
          <span><span className="block text-sm font-medium">{t("iAmDj")}</span><span className="block text-xs text-muted-foreground">{t("iAmDjHint")}</span></span>
        </label>
        {p.is_dj && (
          <div className="mt-3">
            <label className="eyebrow">{t("djName")} *</label>
            <input required className="gg-input mt-2" placeholder={p.name} value={p.dj_name || ""} onChange={e => setP({ ...p, dj_name: e.target.value })} data-testid="p-dj-name" />
          </div>
        )}
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div><label className="eyebrow">{t("favTracks")}</label><input className="gg-input mt-2" placeholder={t("upToThreeComma")} data-testid="p-fav-tracks" value={p.favourite_tracks_str ?? (p.favourite_tracks || []).join(", ")} onChange={e => setP({ ...p, favourite_tracks_str: e.target.value })} /></div>
        <div><label className="eyebrow">{t("favEvents")}</label><input className="gg-input mt-2" placeholder={t("upToThreeComma")} data-testid="p-fav-events" value={p.favourite_events_str ?? (p.favourite_events || []).join(", ")} onChange={e => setP({ ...p, favourite_events_str: e.target.value })} /></div>
      </div>
      <div className="mt-6">
        <div className="grid sm:grid-cols-2 gap-4 mb-5" data-testid="p-social">
          <div><label className="eyebrow flex items-center gap-1.5"><Instagram className="w-3.5 h-3.5" />{t("instagram")}</label><input className="gg-input mt-2" placeholder="@handle / instagram.com/…" data-testid="p-instagram" value={p.instagram || ""} onChange={e => setP({ ...p, instagram: e.target.value })} /></div>
          <div><label className="eyebrow flex items-center gap-1.5"><Facebook className="w-3.5 h-3.5" />{t("facebook")}</label><input className="gg-input mt-2" placeholder="facebook.com/…" data-testid="p-facebook" value={p.facebook || ""} onChange={e => setP({ ...p, facebook: e.target.value })} /></div>
          <p className="sm:col-span-2 text-xs text-muted-foreground -mt-2">{t("socialHint")}</p>
        </div>
        <label className="eyebrow">{t("bio")}</label>
        <textarea rows={3} maxLength={280} className="gg-input mt-2" data-testid="p-bio" value={p.bio || ""} onChange={e => setP({ ...p, bio: e.target.value })} />
      </div>
      <div className="mt-8 border-t border-foreground pt-6" data-testid="privacy-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="eyebrow text-primary">{t("privacy_section")}</h2>
          <Link to={`/users/${user.user_id}`} className="eyebrow underline" data-testid="p-view-public">{t("viewPublicProfile")} →</Link>
        </div>
        <div className="mt-4 divide-y divide-foreground border border-foreground">
          {[
            ["attendance_public", t("showAttendancePublicly"), t("showAttendanceHint"), p.attendance_public ?? true, v => setP({ ...p, attendance_public: v })],
            ["accept_messages", t("acceptMessages"), t("acceptMessagesHint"), p.accept_messages ?? true, v => setP({ ...p, accept_messages: v })],
            ["show_location", t("showLocationPublicly"), null, pp.show_location, v => setPP("show_location", v)],
            ["show_bio", t("showBioPublicly"), null, pp.show_bio, v => setPP("show_bio", v)],
            ["show_genres", t("showGenresPublicly"), null, pp.show_genres, v => setPP("show_genres", v)],
          ].map(([k, label, hint, val, set]) => (
            <label key={k} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-muted transition-colors">
              <input type="checkbox" className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]" checked={!!val} onChange={e => set(e.target.checked)} data-testid={`p-${k}`} />
              <span><span className="block text-sm font-medium">{label}</span>{hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}</span>
            </label>
          ))}
        </div>
      </div>
      <CrewSettings />
      <div className="mt-8 flex flex-wrap gap-2">
        <button onClick={save} className="gg-btn-primary" data-testid="p-save">{t("saveChanges")}</button>
        <button type="button" onClick={() => setDelOpen(true)} className="text-xs text-muted-foreground underline hover:text-destructive transition-colors" data-testid="p-delete">{t("deleteAccount")}</button>
        {delOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setDelOpen(false)} data-testid="delete-account-dialog">
            <div onClick={e => e.stopPropagation()} className="gg-card bg-background p-5 sm:p-6 w-full sm:max-w-md space-y-3">
              <h3 className="font-display uppercase text-2xl">{t("deleteAccountQ")}</h3>
              <p className="text-sm leading-relaxed" data-testid="delete-account-explain">{t("deleteAccountExplain")}</p>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setDelOpen(false)} className="gg-btn !py-2 !px-3 text-xs" data-testid="delete-account-cancel">{t("cancel")}</button>
                <button onClick={del} className="gg-btn !py-2 !px-3 text-xs bg-destructive text-destructive-foreground border-destructive" data-testid="delete-account-confirm">{t("deleteAccountConfirm")}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
